/**
 * Service: Seed demo data (employees + competency library) into a target organization.
 * Used by the "Importer des valeurs démos" button on the Company Admin page.
 * Idempotent: existing rows (by email or slug) are not duplicated.
 */

import { supabase } from "@/integrations/supabase/client";

const DEMO_TEMPLATE_ORG_ID = "00000000-0000-0000-0000-000000000001";

interface DemoEmployee {
  first_name: string;
  last_name: string;
  email: string;
  role_title: string;
  hire_date: string;
  work_location: string;
  phone_number: string;
  preferred_language: string;
  avatar_url: string;
  isManager?: boolean;
  /** competency_profiles.id used to seed the employee's "current" profile + scored competencies. */
  current_profile_id?: string;
}

// IDs from the demo template org's competency_profiles table.
const SEED_PROFILE_DIRECTEUR     = "657dde82-c620-5f48-a0db-80108b86b42f";
const SEED_PROFILE_DEVELOPPEUR   = "601f196e-4af2-5d9b-a4dd-60d356bc6f7f";
const SEED_PROFILE_CHARGE_PROJET = "e943873d-610a-5b15-a2d4-2fb407839fef";
const SEED_PROFILE_DESIGNER      = "3810e41e-1d5e-50c6-8e69-6eb6649c2c7b";
const SEED_PROFILE_ANALYSTE      = "080ce576-e5d2-5f48-aeba-061aeb68f24e";

const DEMO_EMPLOYEES: DemoEmployee[] = [
  { first_name: "Sophie",  last_name: "Tremblay", email: "sophie.tremblay@demo.amelio.co", role_title: "Directrice des ressources humaines", hire_date: "2019-03-15", work_location: "Montréal",   phone_number: "514-555-0101", preferred_language: "Français (Canada)", avatar_url: "https://randomuser.me/api/portraits/women/44.jpg", isManager: true, current_profile_id: SEED_PROFILE_DIRECTEUR },
  { first_name: "Marc",    last_name: "Lavoie",   email: "marc.lavoie@demo.amelio.co",     role_title: "Développeur senior",                 hire_date: "2020-08-01", work_location: "Montréal",   phone_number: "514-555-0102", preferred_language: "Français (Canada)", avatar_url: "https://randomuser.me/api/portraits/men/32.jpg", current_profile_id: SEED_PROFILE_DEVELOPPEUR },
  { first_name: "Émilie",  last_name: "Bouchard", email: "emilie.bouchard@demo.amelio.co", role_title: "Cheffe de projet",                   hire_date: "2021-01-10", work_location: "Québec",     phone_number: "418-555-0103", preferred_language: "Français (Canada)", avatar_url: "https://randomuser.me/api/portraits/women/68.jpg", current_profile_id: SEED_PROFILE_CHARGE_PROJET },
  { first_name: "Julien",  last_name: "Côté",     email: "julien.cote@demo.amelio.co",     role_title: "Designer UX",                        hire_date: "2022-05-22", work_location: "Montréal",   phone_number: "514-555-0104", preferred_language: "Français (Canada)", avatar_url: "https://randomuser.me/api/portraits/men/75.jpg", current_profile_id: SEED_PROFILE_DESIGNER },
  { first_name: "Camille", last_name: "Roy",      email: "camille.roy@demo.amelio.co",     role_title: "Analyste financière",                hire_date: "2023-09-04", work_location: "Sherbrooke", phone_number: "819-555-0105", preferred_language: "Français (Canada)", avatar_url: "https://randomuser.me/api/portraits/women/12.jpg", current_profile_id: SEED_PROFILE_ANALYSTE },
];

export interface DemoSeedResult {
  employeesAdded: number;
  groupsAdded: number;
  competenciesAdded: number;
  questionsAdded: number;
  valuesAdded: number;
  valueQuestionsAdded: number;
}

/** Seed 5 demo employees into the target org (skip those already present by email). */
async function seedDemoEmployees(orgId: string): Promise<number> {
  const emails = DEMO_EMPLOYEES.map((e) => e.email);
  const { data: existing, error: fetchErr } = await supabase
    .from("employees")
    .select("id, email")
    .eq("organization_id", orgId)
    .in("email", emails);
  if (fetchErr) throw new Error(fetchErr.message);

  const existingEmails = new Set((existing ?? []).map((e) => e.email));
  const toInsert = DEMO_EMPLOYEES.filter((e) => !existingEmails.has(e.email));
  if (toInsert.length === 0) return 0;

  const { data: inserted, error: insertErr } = await supabase
    .from("employees")
    .insert(
      toInsert.map((e) => ({
        organization_id: orgId,
        first_name: e.first_name,
        last_name: e.last_name,
        email: e.email,
        role_title: e.role_title,
        hire_date: e.hire_date,
        work_location: e.work_location,
        phone_number: e.phone_number,
        preferred_language: e.preferred_language,
        avatar_url: e.avatar_url,
      }))
    )
    .select("id, email");
  if (insertErr) throw new Error(insertErr.message);

  // Wire Sophie (manager) as the manager of the 4 others.
  const all = [...(existing ?? []), ...(inserted ?? [])];
  const managerRow = all.find((e) => e.email === "sophie.tremblay@demo.amelio.co");
  const reportEmails = DEMO_EMPLOYEES.filter((e) => !e.isManager).map((e) => e.email);
  const reportIds = all.filter((e) => reportEmails.includes(e.email)).map((e) => e.id);
  if (managerRow && reportIds.length > 0) {
    await supabase
      .from("employees")
      .update({ manager_id: managerRow.id })
      .in("id", reportIds);
  }

  return inserted?.length ?? 0;
}

/**
 * Copy competency groups + competencies + questions from the demo template org
 * into the target org. Skips groups already present in the target (matched by slug).
 */
async function seedDemoCompetencies(orgId: string): Promise<{ groups: number; comps: number; questions: number }> {
  if (orgId === DEMO_TEMPLATE_ORG_ID) {
    return { groups: 0, comps: 0, questions: 0 };
  }

  // Fetch source template
  const { data: srcGroups, error: gErr } = await supabase
    .from("competency_groups")
    .select("id, name, slug, description, sort_order")
    .eq("organization_id", DEMO_TEMPLATE_ORG_ID)
    .order("sort_order");
  if (gErr) throw new Error(gErr.message);
  if (!srcGroups || srcGroups.length === 0) return { groups: 0, comps: 0, questions: 0 };

  const { data: existingGroups } = await supabase
    .from("competency_groups")
    .select("slug")
    .eq("organization_id", orgId);
  const existingSlugs = new Set((existingGroups ?? []).map((g) => g.slug));

  const groupsToInsert = srcGroups.filter((g) => !existingSlugs.has(g.slug));
  if (groupsToInsert.length === 0) return { groups: 0, comps: 0, questions: 0 };

  const { data: insertedGroups, error: giErr } = await supabase
    .from("competency_groups")
    .insert(
      groupsToInsert.map((g) => ({
        organization_id: orgId,
        name: g.name,
        slug: g.slug,
        description: g.description,
        sort_order: g.sort_order,
      }))
    )
    .select("id, slug");
  if (giErr) throw new Error(giErr.message);

  // Map src group id → new group id (via slug)
  const newGroupBySlug = new Map((insertedGroups ?? []).map((g) => [g.slug, g.id] as const));
  const srcGroupIdToSlug = new Map(srcGroups.map((g) => [g.id, g.slug] as const));
  const insertedSlugs = new Set(newGroupBySlug.keys());

  // Fetch src competencies for the inserted groups only
  const srcGroupIds = srcGroups.filter((g) => insertedSlugs.has(g.slug)).map((g) => g.id);
  const { data: srcComps, error: cErr } = await supabase
    .from("competencies")
    .select("id, group_id, name, slug, description, sort_order")
    .in("group_id", srcGroupIds);
  if (cErr) throw new Error(cErr.message);

  let competenciesAdded = 0;
  let questionsAdded = 0;

  if (srcComps && srcComps.length > 0) {
    const compsPayload = srcComps.map((c) => {
      const srcSlug = srcGroupIdToSlug.get(c.group_id)!;
      return {
        group_id: newGroupBySlug.get(srcSlug)!,
        name: c.name,
        slug: c.slug,
        description: c.description,
        sort_order: c.sort_order,
      };
    });

    const { data: insertedComps, error: ciErr } = await supabase
      .from("competencies")
      .insert(compsPayload)
      .select("id, slug, group_id");
    if (ciErr) throw new Error(ciErr.message);
    competenciesAdded = insertedComps?.length ?? 0;

    // Map src competency id → new competency id (slug+group)
    const compKey = (slug: string, groupId: string) => `${groupId}::${slug}`;
    const newCompByKey = new Map((insertedComps ?? []).map((c) => [compKey(c.slug, c.group_id), c.id] as const));
    const srcCompIdToNewId = new Map<string, string>();
    for (const sc of srcComps) {
      const srcSlug = srcGroupIdToSlug.get(sc.group_id)!;
      const newGid = newGroupBySlug.get(srcSlug)!;
      const newCid = newCompByKey.get(compKey(sc.slug, newGid));
      if (newCid) srcCompIdToNewId.set(sc.id, newCid);
    }

    const srcCompIds = srcComps.map((c) => c.id);
    const { data: srcQuestions, error: qErr } = await supabase
      .from("competency_questions")
      .select("competency_id, question_text, question_type, options, sort_order, is_active, is_conditional")
      .in("competency_id", srcCompIds);
    if (qErr) throw new Error(qErr.message);

    if (srcQuestions && srcQuestions.length > 0) {
      const qPayload = srcQuestions
        .map((q) => {
          const newCid = srcCompIdToNewId.get(q.competency_id);
          if (!newCid) return null;
          return {
            competency_id: newCid,
            question_text: q.question_text,
            question_type: q.question_type,
            options: q.options,
            sort_order: q.sort_order,
            is_active: q.is_active,
            is_conditional: q.is_conditional,
          };
        })
        .filter((q): q is NonNullable<typeof q> => q !== null);

      if (qPayload.length > 0) {
        const { error: qiErr } = await supabase.from("competency_questions").insert(qPayload);
        if (qiErr) throw new Error(qiErr.message);
        questionsAdded = qPayload.length;
      }
    }
  }

  return { groups: insertedGroups?.length ?? 0, comps: competenciesAdded, questions: questionsAdded };
}

/**
 * Copy company values + their evaluation questions from the demo template org
 * into the target org. Skips values already present (matched by name).
 */
async function seedDemoValues(orgId: string): Promise<{ values: number; questions: number }> {
  if (orgId === DEMO_TEMPLATE_ORG_ID) return { values: 0, questions: 0 };

  const { data: srcValues, error: vErr } = await supabase
    .from("company_values")
    .select("id, name, description, icon, gradient, sort_order")
    .eq("organization_id", DEMO_TEMPLATE_ORG_ID)
    .order("sort_order");
  if (vErr) throw new Error(vErr.message);
  if (!srcValues || srcValues.length === 0) return { values: 0, questions: 0 };

  const { data: existingValues } = await supabase
    .from("company_values")
    .select("name")
    .eq("organization_id", orgId);
  const existingNames = new Set((existingValues ?? []).map((v) => v.name));

  const valuesToInsert = srcValues.filter((v) => !existingNames.has(v.name));
  if (valuesToInsert.length === 0) return { values: 0, questions: 0 };

  const { data: insertedValues, error: viErr } = await supabase
    .from("company_values")
    .insert(
      valuesToInsert.map((v) => ({
        organization_id: orgId,
        name: v.name,
        description: v.description,
        icon: v.icon,
        gradient: v.gradient,
        sort_order: v.sort_order,
      }))
    )
    .select("id, name");
  if (viErr) throw new Error(viErr.message);

  const newValueByName = new Map((insertedValues ?? []).map((v) => [v.name, v.id] as const));
  const srcValueIdToName = new Map(srcValues.map((v) => [v.id, v.name] as const));

  const insertedSrcValueIds = srcValues
    .filter((v) => newValueByName.has(v.name))
    .map((v) => v.id);

  let questionsAdded = 0;
  if (insertedSrcValueIds.length > 0) {
    const { data: srcQuestions, error: qErr } = await supabase
      .from("value_questions")
      .select("value_id, question_text, question_type, options, sort_order, is_active, is_conditional")
      .in("value_id", insertedSrcValueIds);
    if (qErr) throw new Error(qErr.message);

    if (srcQuestions && srcQuestions.length > 0) {
      const qPayload = srcQuestions
        .map((q) => {
          const name = srcValueIdToName.get(q.value_id);
          const newVid = name ? newValueByName.get(name) : null;
          if (!newVid) return null;
          return {
            value_id: newVid,
            question_text: q.question_text,
            question_type: q.question_type,
            options: q.options,
            sort_order: q.sort_order,
            is_active: q.is_active,
            is_conditional: q.is_conditional,
          };
        })
        .filter((q): q is NonNullable<typeof q> => q !== null);

      if (qPayload.length > 0) {
        const { error: qiErr } = await supabase.from("value_questions").insert(qPayload);
        if (qiErr) throw new Error(qiErr.message);
        questionsAdded = qPayload.length;
      }
    }
  }

  return { values: insertedValues?.length ?? 0, questions: questionsAdded };
}

/** Run the full demo seed (employees + competency library). */
export async function importDemoData(orgId: string): Promise<DemoSeedResult> {
  const employeesAdded = await seedDemoEmployees(orgId);
  const { groups, comps, questions } = await seedDemoCompetencies(orgId);
  const { values, questions: valueQs } = await seedDemoValues(orgId);
  // Seed evaluations (1 completed per past period + 1 in-progress for the active period)
  // and a "current" competency profile + per-competency scores for each demo employee.
  // This replaces the hardcoded mock data that used to drive the profile UI.
  await seedDemoEmployeesEvaluations(orgId);
  return {
    employeesAdded,
    groupsAdded: groups,
    competenciesAdded: comps,
    questionsAdded: questions,
    valuesAdded: values,
    valueQuestionsAdded: valueQs,
  };
}

// ─── Demo scenario employee provisioning ─────────────────────────────

export type DemoArchetype = "fonceuse" | "procrastinateur" | "perle";
export type DemoGenre = "homme" | "femme";

interface DemoPersona {
  nom: string;
  genre: DemoGenre;
}

const AVATAR_FEMME: Record<DemoArchetype, string> = {
  fonceuse: "https://randomuser.me/api/portraits/women/65.jpg",
  procrastinateur: "https://randomuser.me/api/portraits/women/22.jpg",
  perle: "https://randomuser.me/api/portraits/women/47.jpg",
};
const AVATAR_HOMME: Record<DemoArchetype, string> = {
  fonceuse: "https://randomuser.me/api/portraits/men/65.jpg",
  procrastinateur: "https://randomuser.me/api/portraits/men/22.jpg",
  perle: "https://randomuser.me/api/portraits/men/47.jpg",
};

/** Profile IDs from the demo template org's competency_profiles table. */
const PROFILE_ANALYSTE = "080ce576-e5d2-5f48-aeba-061aeb68f24e";
const PROFILE_GESTIONNAIRE = "c431ae81-b387-522a-b8ba-b47eff051158";
const PROFILE_CONSEILLER = "a4ba2165-1f3b-5057-acb9-249dfc9dc373";
const PROFILE_SPECIALISTE = "de7c71ac-62a8-5ef8-a191-9b1d4655493e";
const PROFILE_CHARGE_PROJET = "e943873d-610a-5b15-a2d4-2fb407839fef";
const PROFILE_DESIGNER = "3810e41e-1d5e-50c6-8e69-6eb6649c2c7b";

interface ArchetypeConfig {
  role_title: string;
  desired_position_title: string;
  current_profile_id: string;
  desired_profile_id: string;
  department_label: string;
  mandate: string;
  career_timeline: Array<{ year: number; title: string; is_target: boolean; current?: boolean }>;
  competency_profile: { current_role: string; desired_role: string; mastered: string[]; to_develop: string[] };
  scores: { perf: number; pot: number; perfLvl: string; potLvl: string; box: string };
  evaluation_comment: (firstName: string) => string;
}

const ARCHETYPE_CONFIG: Record<DemoArchetype, ArchetypeConfig> = {
  fonceuse: {
    role_title: "Analyste senior",
    desired_position_title: "Gestionnaire",
    current_profile_id: PROFILE_ANALYSTE,
    desired_profile_id: PROFILE_GESTIONNAIRE,
    department_label: "Finance",
    mandate:
      "Analyser les indicateurs de performance des projets et formuler des recommandations stratégiques.",
    career_timeline: [
      { year: 2020, title: "Stagiaire", is_target: false },
      { year: 2021, title: "Analyste Junior", is_target: false },
      { year: 2022, title: "Analyste Junior", is_target: false },
      { year: 2023, title: "Analyste Senior", is_target: false, current: true },
      { year: 2026, title: "Analyste Principal", is_target: true },
      { year: 2028, title: "Gestionnaire", is_target: true },
    ],
    competency_profile: {
      current_role: "Analyste",
      desired_role: "Gestionnaire",
      mastered: [
        "Analyse financière",
        "Modélisation de données",
        "Communication des résultats",
        "Rigueur analytique",
        "Gestion des priorités",
      ],
      to_develop: [
        "Vision stratégique",
        "Allocation de ressources",
        "Mobilisation d'équipe",
        "Courage managérial",
        "Prise de décision éclairée",
        "Soutien au développement",
      ],
    },
    scores: { perf: 4.2, pot: 4.5, perfLvl: "high", potLvl: "high", box: "Étoile" },
    evaluation_comment: (n) =>
      `${n} démontre une forte rigueur analytique et une capacité avérée à influencer les décisions stratégiques. Prête pour un rôle de gestion à court terme.`,
  },
  procrastinateur: {
    role_title: "Conseiller",
    desired_position_title: "Spécialiste",
    current_profile_id: PROFILE_CONSEILLER,
    desired_profile_id: PROFILE_SPECIALISTE,
    department_label: "Service à la clientèle",
    mandate:
      "Conseiller les clients et assurer le suivi des dossiers en respectant les délais convenus.",
    career_timeline: [
      { year: 2019, title: "Agent junior", is_target: false },
      { year: 2021, title: "Agent senior", is_target: false },
      { year: 2023, title: "Conseiller", is_target: false, current: true },
      { year: 2027, title: "Spécialiste", is_target: true },
    ],
    competency_profile: {
      current_role: "Conseiller",
      desired_role: "Spécialiste",
      mastered: [
        "Connaissance produit",
        "Relation client",
        "Communication écrite",
      ],
      to_develop: [
        "Gestion du temps",
        "Priorisation",
        "Respect des échéances",
        "Autonomie d'exécution",
        "Suivi proactif",
      ],
    },
    scores: { perf: 2.4, pot: 3.0, perfLvl: "low", potLvl: "medium", box: "À recadrer" },
    evaluation_comment: (n) =>
      `${n} possède de bonnes connaissances techniques mais peine à respecter les échéances. Un plan d'amélioration encadré est nécessaire à court terme.`,
  },
  perle: {
    role_title: "Designer UX senior",
    desired_position_title: "Directrice de programme",
    current_profile_id: PROFILE_DESIGNER,
    desired_profile_id: PROFILE_GESTIONNAIRE,
    department_label: "Produit",
    mandate:
      "Concevoir des expériences utilisateurs intuitives et innovantes pour les produits internes.",
    career_timeline: [
      { year: 2017, title: "Designer UX junior", is_target: false },
      { year: 2020, title: "Designer UX intermédiaire", is_target: false },
      { year: 2023, title: "Designer UX senior", is_target: false, current: true },
      { year: 2027, title: "Lead designer", is_target: true },
    ],
    competency_profile: {
      current_role: "Designer UX senior",
      desired_role: "Lead designer",
      mastered: [
        "Recherche utilisateur",
        "Conception d'interfaces",
        "Prototypage",
        "Design system",
        "Collaboration produit",
        "Communication visuelle",
      ],
      to_develop: [
        "Vision design long terme",
        "Mentorat",
      ],
    },
    scores: { perf: 4.6, pot: 4.4, perfLvl: "high", potLvl: "high", box: "Étoile" },
    evaluation_comment: (n) =>
      `${n} a livré des résultats exceptionnels au cours des dernières années. Un signal d'alerte récent : baisse d'engagement à surveiller.`,
  },
};

function avatarFor(archetype: DemoArchetype, genre: DemoGenre): string {
  return genre === "homme" ? AVATAR_HOMME[archetype] : AVATAR_FEMME[archetype];
}

/**
 * Provision (or update) one demo employee for the given archetype.
 * Idempotent: same demo_client_id + archetype always resolves to the same row.
 * Also assigns current/desired competency profiles and seeds historical evaluations.
 */
export async function seedDemoArchetypeEmployee(
  archetype: DemoArchetype,
  demoClientId: string,
  personas: Record<DemoArchetype, DemoPersona>,
  managerUserId?: string | null,
): Promise<string> {
  const cfg = ARCHETYPE_CONFIG[archetype];
  const persona = personas[archetype];
  const firstName = persona?.nom?.trim() || archetype;
  const genre: DemoGenre = persona?.genre ?? "femme";

  // Look up the manager (the connected user's employee row), if any.
  let managerEmployeeId: string | null = null;
  if (managerUserId) {
    const { data: mgr } = await supabase
      .from("employees")
      .select("id")
      .eq("user_id", managerUserId)
      .maybeSingle();
    managerEmployeeId = mgr?.id ?? null;
  }

  const payload = {
    organization_id: DEMO_TEMPLATE_ORG_ID,
    is_demo: true,
    demo_client_id: demoClientId,
    is_active: true,
    first_name: firstName,
    last_name: "Démo",
    email: `demo+${demoClientId}+${archetype}@amelio.demo`,
    role_title: cfg.role_title,
    desired_position_title: cfg.desired_position_title,
    avatar_url: avatarFor(archetype, genre),
    work_location: "Montréal",
    preferred_language: "Français (Canada)",
    mandate: cfg.mandate,
    manager_id: managerEmployeeId,
    career_timeline: cfg.career_timeline,
    demo_metadata: {
      archetype,
      department_label: cfg.department_label,
      competency_profile: cfg.competency_profile,
    },
  };

  // Find or insert
  const { data: existing } = await supabase
    .from("employees")
    .select("id")
    .eq("is_demo", true)
    .eq("demo_client_id", demoClientId)
    .eq("email", payload.email)
    .maybeSingle();

  let employeeId: string;
  if (existing?.id) {
    const { error } = await supabase.from("employees").update(payload).eq("id", existing.id);
    if (error) throw new Error(error.message);
    employeeId = existing.id;
  } else {
    const { data: inserted, error } = await supabase
      .from("employees")
      .insert(payload)
      .select("id")
      .single();
    if (error || !inserted) throw new Error(error?.message ?? "Failed to seed demo employee");
    employeeId = inserted.id;
  }

  await assignDemoCompetencyProfiles(employeeId, cfg.current_profile_id, cfg.desired_profile_id);
  await seedDemoEvaluationsFor(employeeId, firstName, cfg);
  await seedDemoCompetencyScoresFor(employeeId, cfg);

  return employeeId;
}

/** Backward-compatible wrapper used by DemoContext for scenario 1. */
export async function seedDemoFonceuse(
  demoClientId: string,
  personas: Record<DemoArchetype, DemoPersona>,
  managerUserId?: string | null,
): Promise<string> {
  // Provision all 3 demo personas so any scenario has fully populated data.
  const fonceuseId = await seedDemoArchetypeEmployee("fonceuse", demoClientId, personas, managerUserId);
  await seedDemoArchetypeEmployee("procrastinateur", demoClientId, personas, managerUserId);
  await seedDemoArchetypeEmployee("perle", demoClientId, personas, managerUserId);
  return fonceuseId;
}

/** Idempotently assign current + desired competency profiles to a demo employee. */
async function assignDemoCompetencyProfiles(
  employeeId: string,
  currentProfileId: string,
  desiredProfileId: string,
): Promise<void> {
  await supabase.from("employee_competency_profiles").delete().eq("employee_id", employeeId);
  await supabase.from("employee_competency_profiles").insert([
    { employee_id: employeeId, profile_id: currentProfileId, context: "current" },
    { employee_id: employeeId, profile_id: desiredProfileId, context: "desired" },
  ]);
}

/**
 * Seed a talent assessment and one completed evaluation per evaluation_period
 * of the demo org, so the demo employee shows a real evaluation history.
 * Idempotent per (employee, period).
 */
async function seedDemoEvaluationsFor(
  employeeId: string,
  firstName: string,
  cfg: ArchetypeConfig,
): Promise<void> {
  // 1) Talent assessment (badge on the card)
  const { data: existingTalent } = await supabase
    .from("talent_assessments")
    .select("id")
    .eq("employee_id", employeeId)
    .limit(1)
    .maybeSingle();

  if (!existingTalent) {
    await supabase.from("talent_assessments").insert({
      employee_id: employeeId,
      performance_score: cfg.scores.perf,
      potential_score: cfg.scores.pot,
      performance_level: cfg.scores.perfLvl,
      potential_level: cfg.scores.potLvl,
      box_category: cfg.scores.box,
      notes: `Évaluation de démonstration : ${cfg.scores.box}.`,
    });
  }

  // 2) One evaluation per evaluation_period of the demo org (skip existing)
  const { data: periods } = await supabase
    .from("evaluation_periods")
    .select("id, end_date")
    .eq("organization_id", DEMO_TEMPLATE_ORG_ID)
    .order("end_date", { ascending: true });

  if (!periods || periods.length === 0) return;

  const { data: existingEvals } = await supabase
    .from("evaluations")
    .select("evaluation_period_id")
    .eq("employee_id", employeeId);
  const seen = new Set((existingEvals ?? []).map((e) => e.evaluation_period_id));

  const toInsert = periods
    .filter((p) => !seen.has(p.id))
    .map((p, idx) => {
      // Slight variation across years to feel realistic.
      const drift = (periods.length - 1 - idx) * 0.15;
      return {
        evaluation_period_id: p.id,
        employee_id: employeeId,
        performance_score: Math.max(1, Math.min(5, cfg.scores.perf - drift)),
        potential_score: Math.max(1, Math.min(5, cfg.scores.pot - drift)),
        performance_level: cfg.scores.perfLvl,
        potential_level: cfg.scores.potLvl,
        overall_comments: cfg.evaluation_comment(firstName),
        status: "completed" as const,
        submitted_at: `${p.end_date}T17:00:00Z`,
        current_step: 5,
        include_company_values: true,
      };
    });

  if (toInsert.length > 0) {
    await supabase.from("evaluations").insert(toInsert);
  }
}

/**
 * Insert per-period employee_competency_scores for every competency in the
 * employee's "current" profile. This is what the "Évaluation des compétences"
 * UI reads to render the mastery list. Idempotent via the unique
 * (employee, competency, period) index.
 */
async function seedDemoCompetencyScoresFor(
  employeeId: string,
  cfg: ArchetypeConfig,
): Promise<void> {
  const { data: items } = await supabase
    .from("competency_profile_items")
    .select("competency_id")
    .eq("profile_id", cfg.current_profile_id);
  if (!items || items.length === 0) return;

  const { data: periods } = await supabase
    .from("evaluation_periods")
    .select("id, end_date")
    .eq("organization_id", DEMO_TEMPLATE_ORG_ID)
    .order("end_date", { ascending: true });
  if (!periods || periods.length === 0) return;

  // Per-archetype progression across periods (oldest → newest).
  const progression: Record<DemoArchetype, Array<"junior" | "evolution" | "satisfactory" | "advanced" | "expert">> = {
    fonceuse: ["evolution", "satisfactory", "advanced"],
    procrastinateur: ["junior", "evolution", "evolution"],
    perle: ["advanced", "expert", "advanced"],
  };
  const archetype = (cfg.competency_profile.current_role.toLowerCase().includes("analyste")
    ? "fonceuse"
    : cfg.competency_profile.current_role.toLowerCase().includes("conseiller")
      ? "procrastinateur"
      : "perle") as DemoArchetype;
  const levels = progression[archetype];

  type Lvl = "junior" | "evolution" | "satisfactory" | "advanced" | "expert";
  const rows: Array<{
    employee_id: string;
    competency_id: string;
    evaluation_period_id: string;
    assessed_level: Lvl;
    assessment_date: string;
  }> = [];

  periods.forEach((p, pIdx) => {
    const baseLevel = levels[Math.min(pIdx, levels.length - 1)];
    items.forEach((it, cIdx) => {
      // Slight variation across competencies so the list isn't uniform.
      const order: Lvl[] = ["junior", "evolution", "satisfactory", "advanced", "expert"];
      const baseIdx = order.indexOf(baseLevel);
      const offset = (cIdx % 3) - 1; // -1, 0, +1
      const finalIdx = Math.max(0, Math.min(order.length - 1, baseIdx + offset));
      rows.push({
        employee_id: employeeId,
        competency_id: it.competency_id,
        evaluation_period_id: p.id,
        assessed_level: order[finalIdx],
        assessment_date: `${p.end_date}T17:00:00Z`,
      });
    });
  });

  if (rows.length === 0) return;
  await supabase
    .from("employee_competency_scores")
    .upsert(rows, { onConflict: "employee_id,competency_id,evaluation_period_id" });
}

/**
 * Delete all demo employees attached to a demo_client_id.
 * Used when the user restarts a demo scenario from scratch.
 */
export async function resetDemoEmployees(demoClientId: string): Promise<void> {
  const { error } = await supabase
    .from("employees")
    .delete()
    .eq("is_demo", true)
    .eq("demo_client_id", demoClientId);
  if (error) throw new Error(error.message);
}

// ─── Seed evaluations + competency scores for the 5 named demo employees ────

const SEED_LEVELS: Array<"junior" | "evolution" | "satisfactory" | "advanced" | "expert"> = [
  "junior",
  "evolution",
  "satisfactory",
  "advanced",
  "expert",
];

/** Seed scores per profile to give each employee a distinct progression. */
const SEED_SCORE_BY_EMAIL: Record<string, { perf: number; pot: number; perfLvl: string; potLvl: string; comment: string }> = {
  "sophie.tremblay@demo.amelio.co": { perf: 4.4, pot: 4.5, perfLvl: "high", potLvl: "high", comment: "Leadership RH solide et vision stratégique affirmée." },
  "marc.lavoie@demo.amelio.co":     { perf: 4.0, pot: 4.2, perfLvl: "high", potLvl: "high", comment: "Excellente expertise technique et bon mentorat de l'équipe." },
  "emilie.bouchard@demo.amelio.co": { perf: 3.8, pot: 3.9, perfLvl: "high", potLvl: "moderate", comment: "Pilote ses projets avec rigueur, prête pour des dossiers plus complexes." },
  "julien.cote@demo.amelio.co":     { perf: 3.6, pot: 4.0, perfLvl: "moderate", potLvl: "high", comment: "Sens du design affirmé, fort potentiel de croissance vers le lead UX." },
  "camille.roy@demo.amelio.co":     { perf: 3.4, pot: 3.6, perfLvl: "moderate", potLvl: "moderate", comment: "Apporte des analyses pertinentes, gagne en autonomie chaque trimestre." },
};

/**
 * For each of the 5 named demo employees:
 *  - Assign a "current" competency profile (idempotent).
 *  - Insert one COMPLETED evaluation per completed evaluation_period (skip existing).
 *  - Insert one IN-PROGRESS (draft, current_step=3) evaluation for the active period.
 *  - Insert per-competency assessed levels for completed periods.
 * All operations are idempotent and safe to re-run.
 */
async function seedDemoEmployeesEvaluations(orgId: string): Promise<void> {
  const emails = DEMO_EMPLOYEES.map((e) => e.email);
  const { data: emps } = await supabase
    .from("employees")
    .select("id, email")
    .eq("organization_id", orgId)
    .in("email", emails);
  if (!emps || emps.length === 0) return;

  const { data: periods } = await supabase
    .from("evaluation_periods")
    .select("id, end_date, status")
    .eq("organization_id", orgId)
    .order("end_date", { ascending: true });
  if (!periods || periods.length === 0) return;

  const completedPeriods = periods.filter((p) => p.status === "completed");
  const activePeriod = periods.find((p) => p.status === "active") ?? null;

  for (const emp of emps) {
    const meta = DEMO_EMPLOYEES.find((d) => d.email === emp.email);
    if (!meta) continue;
    const scoreCfg = SEED_SCORE_BY_EMAIL[emp.email];
    if (!scoreCfg) continue;

    // 1) Assign current competency profile if missing.
    if (meta.current_profile_id) {
      const { data: existingProfile } = await supabase
        .from("employee_competency_profiles")
        .select("id")
        .eq("employee_id", emp.id)
        .eq("context", "current")
        .maybeSingle();
      if (!existingProfile) {
        await supabase.from("employee_competency_profiles").insert({
          employee_id: emp.id,
          profile_id: meta.current_profile_id,
          context: "current",
        });
      }
    }

    // 2) Existing evaluations for this employee, to skip duplicates.
    const { data: existingEvals } = await supabase
      .from("evaluations")
      .select("evaluation_period_id, status")
      .eq("employee_id", emp.id);
    const completedSeen = new Set(
      (existingEvals ?? []).filter((e) => e.status === "completed").map((e) => e.evaluation_period_id),
    );
    const draftSeen = new Set(
      (existingEvals ?? []).filter((e) => e.status === "draft").map((e) => e.evaluation_period_id),
    );

    // Pre-fetch competency IDs from the assigned profile so evaluations are not
    // treated as thematic meetings (which require a non-empty competency_ids array).
    let profileCompetencyIds: string[] = [];
    if (meta.current_profile_id) {
      const { data: profileItems } = await supabase
        .from("competency_profile_items")
        .select("competency_id")
        .eq("profile_id", meta.current_profile_id);
      profileCompetencyIds = (profileItems ?? []).map((i) => i.competency_id);
    }

    // 3) Completed evaluations (one per past period).
    const completedRows = completedPeriods
      .filter((p) => !completedSeen.has(p.id))
      .map((p, idx) => {
        const drift = (completedPeriods.length - 1 - idx) * 0.15;
        return {
          evaluation_period_id: p.id,
          employee_id: emp.id,
          performance_score: Math.max(1, Math.min(5, scoreCfg.perf - drift)),
          potential_score: Math.max(1, Math.min(5, scoreCfg.pot - drift)),
          performance_level: scoreCfg.perfLvl,
          potential_level: scoreCfg.potLvl,
          overall_comments: scoreCfg.comment,
          status: "completed" as const,
          submitted_at: `${p.end_date}T17:00:00Z`,
          current_step: 5,
          include_company_values: true,
          competency_ids: profileCompetencyIds,
        };
      });
    if (completedRows.length > 0) {
      await supabase.from("evaluations").insert(completedRows);
    }

    // 4) In-progress evaluation for the active period.
    if (activePeriod && !draftSeen.has(activePeriod.id)) {
      await supabase.from("evaluations").insert({
        evaluation_period_id: activePeriod.id,
        employee_id: emp.id,
        status: "draft" as const,
        current_step: 3,
        include_company_values: true,
        competency_ids: profileCompetencyIds,
      });
    }

    // 5) Per-competency scores for completed periods.
    if (meta.current_profile_id && completedPeriods.length > 0) {
      const items = profileCompetencyIds.map((competency_id) => ({ competency_id }));
      if (items.length > 0) {
        const baseIdx = SEED_LEVELS.indexOf(
          scoreCfg.perfLvl === "high" ? "advanced" : scoreCfg.perfLvl === "moderate" ? "satisfactory" : "evolution",
        );
        const rows: Array<{
          employee_id: string;
          competency_id: string;
          evaluation_period_id: string;
          assessed_level: (typeof SEED_LEVELS)[number];
          assessment_date: string;
        }> = [];
        completedPeriods.forEach((p, pIdx) => {
          const periodOffset = pIdx - (completedPeriods.length - 1); // negative for older periods
          items.forEach((it, cIdx) => {
            const compOffset = (cIdx % 3) - 1; // -1, 0, +1
            const finalIdx = Math.max(
              0,
              Math.min(SEED_LEVELS.length - 1, baseIdx + periodOffset + compOffset),
            );
            rows.push({
              employee_id: emp.id,
              competency_id: it.competency_id,
              evaluation_period_id: p.id,
              assessed_level: SEED_LEVELS[finalIdx],
              assessment_date: `${p.end_date}T17:00:00Z`,
            });
          });
        });
        if (rows.length > 0) {
          await supabase
            .from("employee_competency_scores")
            .upsert(rows, { onConflict: "employee_id,competency_id,evaluation_period_id" });
        }
      }
    }
  }
}